import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import { getCoreGroups } from '../objects/group_service.js?v=20260904c';
import {
  activateDrawingSheet,
  activeDrawingSheetCoreGroups,
  createDrawingSheet,
  deleteDrawingSheet,
  drawingSheetHasContent,
  drawingSheetNavigationModel,
  reorderDrawingSheets,
  suggestedNewDrawingSheetTitle
} from '../project/drawing_sheet_service.js?v=20260904c';
import { openDrawingSheetCreateModal } from '../project/drawing_sheet_modal.js?v=20260826a';
import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import { openConfirmDialog } from '../ui/dialog_service.js?v=20260817b';
import { escapeHtml } from '../utils/html_utils.js?v=20260706d';
import { renderObjectList } from './sidebar_object_list.js?v=20260706d';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import {
  coreGroupWorkflowStatusDefinition,
  normalizeCoreGroupWorkflowStatus
} from '../objects/core_group_workflow_model.mjs?v=20260903a';

// Drag&Drop-Zustand fuer das Umsortieren der Zeichnungsebenen-Reihenfolge.
// Modul-weiter State ist hier unproblematisch, weil immer nur eine
// Drag-Operation gleichzeitig laufen kann und jeder renderGroupList()-Aufruf
// den Wrapper (und damit alle daran gebundenen Listener) komplett neu
// aufbaut -- kein Risiko doppelt registrierter Listener ueber mehrere
// Renders hinweg (anders als bei Listenern direkt auf dem stabilen
// #groupList-Host, die sich bei jedem Render aufaddieren wuerden).
let draggingDrawingSheetId = null;
let dragPlaceholderEl = null;

function ensureDrawingSheetDragPlaceholder() {
  if (!dragPlaceholderEl) {
    dragPlaceholderEl = document.createElement('div');
    dragPlaceholderEl.className = 'drawing-sheet-drop-placeholder';
    dragPlaceholderEl.setAttribute('aria-hidden', 'true');
  }
  return dragPlaceholderEl;
}

function clearDrawingSheetDragPlaceholder() {
  if (dragPlaceholderEl?.parentElement) dragPlaceholderEl.parentElement.removeChild(dragPlaceholderEl);
}

function cleanupDrawingSheetDrag(sheetsHost) {
  clearDrawingSheetDragPlaceholder();
  draggingDrawingSheetId = null;
  sheetsHost?.querySelectorAll('.drawing-sheet-section.is-dragging')
    .forEach(el => el.classList.remove('is-dragging'));
}

function bindDrawingSheetDragHandle(title, section, sheet, sheetsHost) {
  title.draggable = true;
  title.addEventListener('dragstart', event => {
    draggingDrawingSheetId = sheet.id;
    section.classList.add('is-dragging');
    event.dataTransfer.effectAllowed = 'move';
    // Manche Browser brauchen zwingend echte Daten im dataTransfer, sonst
    // greift dragover/drop nicht zuverlaessig.
    event.dataTransfer.setData('text/plain', sheet.id);
  });
  title.addEventListener('dragend', () => cleanupDrawingSheetDrag(sheetsHost));
}

function bindDrawingSheetListDragAndDrop(sheetsHost) {
  sheetsHost.addEventListener('dragover', event => {
    if (!draggingDrawingSheetId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    // Sobald der Platzhalter irgendwo eingefuegt wird, verschieben sich die
    // Sections darunter -- der Mauszeiger (der sich ja nicht bewegt hat)
    // steht danach oft direkt UEBER dem gerade eingefuegten Platzhalter.
    // closest('.drawing-sheet-section') liefert dafuer null, was ohne diese
    // fruehe Rueckkehr in den "leerer Bereich"-Zweig unten liefe und den
    // Platzhalter ans Ende verschieben wuerde -- von dort loest der naechste
    // dragover-Tick (Zeiger wieder ueber der urspruenglichen Section) sofort
    // die Rueckverschiebung aus: eine Oszillation, die als Flackern sichtbar
    // wird. Solange der Zeiger ueber dem Platzhalter selbst steht, bleibt
    // die aktuelle Position deshalb einfach unveraendert.
    if (dragPlaceholderEl && (event.target === dragPlaceholderEl || dragPlaceholderEl.contains(event.target))) {
      return;
    }
    const targetSection = event.target.closest('.drawing-sheet-section');
    const placeholder = ensureDrawingSheetDragPlaceholder();
    if (!targetSection || targetSection.dataset.drawingSheetId === draggingDrawingSheetId) {
      if (!targetSection && sheetsHost.lastElementChild !== placeholder) sheetsHost.appendChild(placeholder);
      return;
    }
    const rect = targetSection.getBoundingClientRect();
    const insertBefore = event.clientY < rect.top + rect.height / 2;
    const desiredSibling = insertBefore ? targetSection : targetSection.nextElementSibling;
    if (placeholder.nextElementSibling === desiredSibling) return;
    sheetsHost.insertBefore(placeholder, desiredSibling);
  });

  sheetsHost.addEventListener('drop', event => {
    event.preventDefault();
    const draggedId = draggingDrawingSheetId;
    if (!draggedId || !dragPlaceholderEl?.parentElement) {
      cleanupDrawingSheetDrag(sheetsHost);
      return;
    }
    // Das urspruengliche Section-Element der gezogenen Ebene bleibt waehrend
    // des Drags an seiner alten Position im DOM stehen (nur der Platzhalter
    // zeigt die neue Position) -- daher hier ausschliessen und stattdessen
    // ausschliesslich ueber die Platzhalter-Position einsetzen.
    const orderedIds = [...sheetsHost.children]
      .filter(el => el.dataset?.drawingSheetId !== draggedId)
      .map(el => (el === dragPlaceholderEl ? draggedId : el.dataset?.drawingSheetId))
      .filter(Boolean);
    cleanupDrawingSheetDrag(sheetsHost);
    if (orderedIds.length < 2) return;
    reorderDrawingSheets(orderedIds);
  });

  sheetsHost.addEventListener('dragleave', event => {
    if (sheetsHost.contains(event.relatedTarget)) return;
    clearDrawingSheetDragPlaceholder();
  });
}

function sidebarGroupMeta(group) {
  return group?.sidebarMeta || group?.metaLabel || group?.detailLabel || group?.productLabel || '';
}

function liveGroupForSummary(summary) {
  return getCoreGroups().find(group => (
    String(group.key || '') === String(summary?.key || '')
    || (
      String(group.groupId || group.id || '') === String(summary?.groupId || summary?.id || '')
      && String(group.systemId || group.systemKey || '') === String(summary?.systemId || summary?.systemKey || '')
    )
  )) || null;
}

function activateCoreGroup(group, event) {
  const systemId = group?.systemId || group?.systemKey || '';
  const system = SystemRegistry.getSystem(systemId);
  if (!system || typeof system.selectCoreGroup !== 'function') return false;
  SystemRegistry.activateSystem(systemId);
  if (SystemRegistry.selectCoreGroup(systemId, group, { event }) !== true) return false;
  AppRuntime.refresh(false);
  return true;
}

function bindSidebarGroupActivation(element, sheet, group, activeSheetId) {
  const activate = async event => {
    if (element.dataset.busy === 'true') return false;
    element.dataset.busy = 'true';
    element.setAttribute('aria-busy', 'true');
    try {
      if (sheet.id !== activeSheetId) {
        const switched = await activateDrawingSheet(sheet.id);
        if (!switched) return false;
      }
      const liveGroup = sheet.id === activeSheetId ? group : liveGroupForSummary(group);
      if (!liveGroup) throw new Error('Gruppe konnte in der Zeichnungsebene nicht gefunden werden.');
      return activateCoreGroup(liveGroup, event);
    } catch (error) {
      console.warn('Zeichnungsebene oder Gruppe konnte nicht aktiviert werden', error);
      setStatus(error.message || 'Zeichnungsebene konnte nicht geladen werden.');
      return false;
    } finally {
      delete element.dataset.busy;
      element.removeAttribute('aria-busy');
    }
  };

  element.addEventListener('click', activate);
  element.addEventListener('keydown', event => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    activate(event);
  });
}

function createGroupElement(sheet, group, activeSheetId) {
  const element = document.createElement('div');
  const sheetActive = sheet.id === activeSheetId;
  const workflowStatus = normalizeCoreGroupWorkflowStatus(group.workflowStatus);
  const statusDefinition = coreGroupWorkflowStatusDefinition(workflowStatus);
  const statusLabel = t(statusDefinition.labelKey, statusDefinition.fallbackLabel);
  element.className = `group-item drawing-sheet-group${sheetActive ? ' is-sheet-active' : ''}${group.active ? ' active' : ''}`;
  element.tabIndex = 0;
  element.setAttribute('role', 'button');
  element.setAttribute(
    'aria-label',
    `${group.displayName}. ${t('sidebar_rechts.gruppenstatus.label.status', 'Status')}: ${statusLabel}`
  );
  element.title = `${t('sidebar_rechts.gruppenstatus.label.status', 'Status')}: ${statusLabel}`;
  element.dataset.drawingSheetId = sheet.id;
  element.dataset.sidebarGroupKey = group.key || '';
  element.dataset.sidebarGroupId = group.groupId || group.id || '';
  element.dataset.sidebarSystemId = group.systemId || group.systemKey || '';
  element.dataset.coreGroupWorkflowStatus = workflowStatus;

  const meta = sidebarGroupMeta(group);
  element.innerHTML = `
    <div><strong>${escapeHtml(group.displayName)}</strong></div>
    ${meta ? `<div class="small-text">${escapeHtml(meta)}</div>` : ''}
  `;
  bindSidebarGroupActivation(element, sheet, group, activeSheetId);
  return element;
}

function bindDrawingSheetTitle(button, sheet, activeSheetId) {
  const activate = async () => {
    if (button.dataset.busy === 'true') return;
    button.dataset.busy = 'true';
    try {
      await activateDrawingSheet(sheet.id, {
        clearSelection: true,
        markDirty: sheet.id !== activeSheetId
      });
    } catch (error) {
      console.warn('Zeichnungsebene konnte nicht aktiviert werden', error);
      setStatus(error.message || 'Zeichnungsebene konnte nicht geladen werden.');
    } finally {
      delete button.dataset.busy;
    }
  };
  button.addEventListener('click', activate);
}

function createDrawingSheetSection(sheet, activeSheetId, liveGroups, sheetCount, sheetsHost) {
  const section = document.createElement('div');
  const active = sheet.id === activeSheetId;
  section.className = `drawing-sheet-section${active ? ' is-active' : ''}`;
  section.dataset.drawingSheetId = sheet.id;

  const title = document.createElement('button');
  title.type = 'button';
  title.className = `drawing-sheet-list-title${active ? ' is-active' : ''}`;
  title.setAttribute('aria-expanded', active ? 'true' : 'false');
  title.innerHTML = `
    ${sheetCount > 1 ? '<span class="drawing-sheet-drag-handle" aria-hidden="true">⠿</span>' : ''}
    <span class="drawing-sheet-list-title-text">${escapeHtml(sheet.title)}</span>
    <span class="drawing-sheet-list-caret${active ? ' is-expanded' : ''}" aria-hidden="true">›</span>
  `;
  bindDrawingSheetTitle(title, sheet, activeSheetId);
  if (sheetCount > 1) bindDrawingSheetDragHandle(title, section, sheet, sheetsHost);
  section.appendChild(title);

  const groupHost = document.createElement('div');
  groupHost.className = 'drawing-sheet-groups stack';
  // Nur die aktive Zeichnungsebene bleibt aufgeklappt, alle anderen
  // kollabieren (Nutzerwunsch: bei vielen Ebenen mit vielen Gruppen wird die
  // Seitenleiste sonst schnell unuebersichtlich).
  groupHost.hidden = !active;
  const groups = active ? liveGroups : sheet.sidebarGroups;
  for (const group of groups) groupHost.appendChild(createGroupElement(sheet, group, activeSheetId));

  const deletable = active && sheetCount > 1 && !drawingSheetHasContent(sheet.id);
  if (deletable) {
    const deleteButton = document.createElement('button');
    deleteButton.type = 'button';
    deleteButton.className = 'drawing-sheet-delete-button';
    deleteButton.textContent = t('sidebar_links.zeichnungsebenen.button.loeschen', 'Zeichnungsebene löschen');
    deleteButton.addEventListener('click', async () => {
      const confirmed = await openConfirmDialog({
        title: t('sidebar_links.zeichnungsebenen.button.loeschen', 'Zeichnungsebene löschen'),
        message: `Soll „${sheet.title}“ endgültig gelöscht werden?`,
        confirmLabel: t('modal.gemeinsam.button.loeschen', 'Löschen'),
        danger: true
      });
      if (!confirmed) return;
      try {
        await deleteDrawingSheet(sheet.id);
        setStatus(`Zeichnungsebene gelöscht: ${sheet.title}`);
      } catch (error) {
        setStatus(error.message || 'Zeichnungsebene konnte nicht gelöscht werden.');
      }
    });
    groupHost.appendChild(deleteButton);
  } else if (!groups.length) {
    const note = document.createElement('div');
    note.className = 'small-text note drawing-sheet-empty-note';
    note.textContent = t('sidebar_links.gruppen.wert.leer', 'Noch keine Gruppen in der Zeichnung.');
    groupHost.appendChild(note);
  }
  section.appendChild(groupHost);
  return section;
}

function bindCreateDrawingSheetButton(button, navigation) {
  button.addEventListener('click', async () => {
    if (button.dataset.busy === 'true') return;
    const result = await openDrawingSheetCreateModal({
      sheets: navigation.sheets,
      suggestedTitle: suggestedNewDrawingSheetTitle()
    });
    if (!result) return;
    button.dataset.busy = 'true';
    button.disabled = true;
    try {
      const created = await createDrawingSheet(result);
      setStatus(`Zeichnungsebene erstellt: ${created?.title || result.title}`);
    } catch (error) {
      console.warn('Zeichnungsebene konnte nicht erstellt werden', error);
      setStatus(error.message || 'Zeichnungsebene konnte nicht erstellt werden.');
    } finally {
      delete button.dataset.busy;
      if (button.isConnected) button.disabled = false;
    }
  });
}

function renderGroupList() {
  const host = document.getElementById('groupList');
  if (!host) {
    renderObjectList();
    return false;
  }
  host.innerHTML = '';
  const navigation = drawingSheetNavigationModel();
  const liveGroups = activeDrawingSheetCoreGroups(getCoreGroups(), { markChanged: false });

  // Eigener, bei jedem Render neu erzeugter Wrapper fuer die Drag&Drop-
  // Listener (siehe bindDrawingSheetListDragAndDrop) -- so binden wir sie nie
  // auf dem stabilen #groupList-Host, wo sie sich ueber mehrere Renders
  // hinweg aufaddieren wuerden.
  const sheetsHost = document.createElement('div');
  sheetsHost.className = 'drawing-sheet-list stack';
  for (const sheet of navigation.sheets) {
    sheetsHost.appendChild(createDrawingSheetSection(
      sheet,
      navigation.activeSheetId,
      liveGroups,
      navigation.sheets.length,
      sheetsHost
    ));
  }
  if (navigation.sheets.length > 1) bindDrawingSheetListDragAndDrop(sheetsHost);
  host.appendChild(sheetsHost);

  const createButton = document.createElement('button');
  createButton.type = 'button';
  createButton.className = 'drawing-sheet-create-button';
  createButton.textContent = t('sidebar_links.zeichnungsebenen.button.neu', 'Neue Zeichnungsebene erstellen');
  bindCreateDrawingSheetButton(createButton, navigation);
  host.appendChild(createButton);

  renderObjectList();
  return liveGroups.length > 0;
}

export { renderGroupList };
